import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, CalendarDays, Swords, ClipboardList,
  Trophy, Building2, Scale, Settings, LogOut, Menu, X,
  ExternalLink, UserPlus, Radio, Newspaper,
} from "lucide-react";
import { Toaster } from "../ui/sonner";
import { useAuth } from "../../context/AuthContext";
import { BuflLogo } from "../Logo";
import { fetchJson } from "../../lib/api";

const ADMIN_LINKS = [
  { to: "/admin", label: "Dashboard", Icon: LayoutDashboard, exact: true, minRole: "editor" },
  { to: "/admin/fighters", label: "Combattants", Icon: Users, minRole: "editor" },
  { to: "/admin/events", label: "Événements", Icon: CalendarDays, minRole: "editor" },
  { to: "/admin/fights", label: "Combats", Icon: Swords, minRole: "editor" },
  { to: "/admin/rankings", label: "Rankings", Icon: Trophy, minRole: "editor" },
  { to: "/admin/ppv", label: "Live / PPV", Icon: Radio, minRole: "editor" },
  { to: "/admin/applications", label: "Candidatures", Icon: UserPlus, minRole: "editor", badgeKey: "pending_applications" },
  { to: "/admin/news", label: "Actualités", Icon: Newspaper, minRole: "editor" },
  { to: "/admin/sponsors", label: "Sponsors", Icon: ClipboardList, minRole: "editor" },
  { to: "/admin/academies", label: "Académies", Icon: Building2, minRole: "editor" },
  { to: "/admin/weight-classes", label: "Catégories", Icon: Scale, minRole: "admin" },
  { to: "/admin/settings", label: "Réglages", Icon: Settings, minRole: "admin" },
  { to: "/admin/users", label: "Utilisateurs", Icon: Users, minRole: "super_admin" },
];

export default function AdminLayout({ children }) {
  const { user, logout, hasRole } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState({});

  useEffect(() => {
    let alive = true;
    const load = () =>
      fetchJson("/admin/stats").then((s) => {
        if (alive) setStats(s || {});
      }).catch(() => {});
    load();
    // Refresh badge count periodically so new applications become visible
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const handleLogout = () => {
    logout();
    nav("/admin/login", { replace: true });
  };

  const availableLinks = ADMIN_LINKS.filter((l) => hasRole(l.minRole));

  return (
    <div className="min-h-screen bg-[#050505] text-neutral-100 flex flex-col lg:flex-row">
      {/* Sidebar (desktop) / Slide-in (mobile) */}
      <aside
        className={`lg:w-60 lg:sticky lg:top-0 lg:h-screen bg-black border-r border-neutral-900 flex flex-col ${
          open ? "fixed inset-0 z-40" : "hidden lg:flex"
        }`}
      >
        <div className="p-5 flex items-center justify-between border-b border-neutral-900">
          <Link to="/admin" onClick={() => setOpen(false)} data-testid="admin-sidebar-logo">
            <BuflLogo compact />
            <p className="text-[10px] font-stats uppercase tracking-[0.3em] text-gold mt-1">
              STAFF
            </p>
          </Link>
          <button
            className="lg:hidden text-neutral-400"
            onClick={() => setOpen(false)}
            aria-label="Fermer le menu"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 py-3 overflow-y-auto">
          {availableLinks.map(({ to, label, Icon, exact, badgeKey }) => {
            const badge = badgeKey ? stats?.[badgeKey] : null;
            return (
              <NavLink
                key={to}
                to={to}
                end={exact}
                onClick={() => setOpen(false)}
                data-testid={`admin-nav-${to.replace("/admin/", "").replace("/admin", "dashboard") || "dashboard"}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-3 text-xs font-display uppercase tracking-widest border-l-2 transition-colors duration-200 ${
                    isActive
                      ? "text-white border-gold bg-neutral-950"
                      : "text-neutral-500 border-transparent hover:text-white hover:bg-neutral-950/50"
                  }`
                }
              >
                <Icon size={14} />
                <span className="flex-1">{label}</span>
                {typeof badge === "number" && badge > 0 && (
                  <span
                    data-testid={`admin-nav-badge-${badgeKey}`}
                    className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-stats font-bold bg-gold text-black rounded-full"
                  >
                    {badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-neutral-900">
          <p className="text-[10px] font-stats uppercase tracking-widest text-neutral-600 truncate">
            {user?.email}
          </p>
          <p className="text-[10px] font-stats uppercase tracking-widest text-gold mt-0.5">
            {user?.role}
          </p>
          <div className="mt-3 flex flex-col gap-2">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[11px] font-display uppercase tracking-widest text-neutral-400 hover:text-white transition-colors duration-200"
            >
              <ExternalLink size={12} /> Voir le site
            </a>
            <button
              data-testid="admin-logout"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 text-[11px] font-display uppercase tracking-widest text-neutral-400 hover:text-burkina-red transition-colors duration-200"
            >
              <LogOut size={12} /> Se déconnecter
            </button>
          </div>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden sticky top-0 z-30 bg-black/90 backdrop-blur border-b border-neutral-900 flex items-center justify-between px-4 h-14">
          <button
            onClick={() => setOpen(true)}
            className="text-neutral-300"
            aria-label="Menu"
            data-testid="admin-mobile-menu"
          >
            <Menu size={20} />
          </button>
          <BuflLogo compact />
          <button
            data-testid="admin-mobile-logout"
            onClick={handleLogout}
            className="text-neutral-400"
            aria-label="Déconnexion"
          >
            <LogOut size={18} />
          </button>
        </header>

        <main className="p-4 md:p-8 max-w-full min-w-0">
          {children}
        </main>
      </div>
      <Toaster />
    </div>
  );
}
